import { MessageSquare, ListChecks, Layout, Sparkles, CheckCircle } from 'lucide-react'
import { useStudioStore } from '@/store/studio-store'
import { useStudioLocale } from '@/i18n'
import { COOGO_IDE_CHROME_COPY } from '@/shell/ide-v2/coogo-ide-chrome-copy'
import { phaseToStepperStep, STEPPER_STEP_ORDER, type StepperStep } from '@/store/conversation-types'
import type { LucideIcon } from 'lucide-react'

// ─── Stepper Phase Config ──────────────────────────────────────
//
// F4 — the five labels used to be English literals here, so the Korean chat
// dock read "Describe / Scenarios / Pages / Generate / Complete". The step
// KEY is structure and stays here; the label is language and lives in the
// copy module, looked up at render time so it follows a locale switch.

interface StepperPhase {
  key: StepperStep
  icon: LucideIcon
}

const STEPPER_PHASES: StepperPhase[] = [
  { key: 'describe', icon: MessageSquare },
  { key: 'scenarios', icon: ListChecks },
  { key: 'pages', icon: Layout },
  { key: 'generate', icon: Sparkles },
  { key: 'complete', icon: CheckCircle },
]

// ─── Component ─────────────────────────────────────────────────

export function PipelineStepper() {
  const { locale } = useStudioLocale()
  const stepperCopy = COOGO_IDE_CHROME_COPY[locale].pipelineStepper
  const phase = useStudioStore((s) => s.conversation.phase)
  const currentStep = phaseToStepperStep(phase)

  // Don't render when idle (no active conversation)
  if (!currentStep) return null
  // Once the app is open, the right rail should return to conversation and
  // review. Keeping the generation checklist visible makes the workspace feel
  // like an internal pipeline instead of a user-led chat.
  if (currentStep === 'complete') return null

  const currentIndex = STEPPER_STEP_ORDER.indexOf(currentStep)

  return (
    <div className="flex items-center justify-between px-4 py-3 border-b border-border">
      {STEPPER_PHASES.map((step, index) => {
        const isCompleted = index < currentIndex
        const isActive = index === currentIndex
        const isFuture = index > currentIndex
        const Icon = step.icon

        return (
          <div key={step.key} className="flex items-center">
            {/* Step circle + label */}
            <div className="flex flex-col items-center gap-1">
              <div
                className={`
                  flex items-center justify-center w-8 h-8 rounded-full transition-colors
                  ${isCompleted ? 'bg-muted text-foreground' : ''}
                  ${isActive ? 'bg-foreground text-background ring-2 ring-border' : ''}
                  ${isFuture ? 'bg-muted text-muted-foreground' : ''}
                `}
              >
                {isCompleted ? (
                  <CheckCircle className="w-4 h-4" />
                ) : (
                  <Icon className="w-4 h-4" />
                )}
              </div>
              <span
                className={`text-xs ${
                  isActive ? 'font-medium text-foreground' : 'text-muted-foreground'
                }`}
              >
                {stepperCopy[step.key]}
              </span>
            </div>

            {/* Connector line (not after last step) */}
            {index < STEPPER_PHASES.length - 1 && (
              <div
                className={`h-0.5 w-8 mx-1 ${
                  index < currentIndex ? 'bg-foreground/30' : 'bg-muted'
                }`}
              />
            )}
          </div>
        )
      })}
    </div>
  )
}
